import React, { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { PIPWidget, TWCAccessibleContent } from '@components';
import { useRecoilState, useRecoilValue } from 'recoil';

import { mapPageTestIDs } from '@constants';
import { useContextNodeHandles, useContextReference, useWeatherMapAlerts } from '@hooks';
import { navigationService } from '@services';
import { mapTypeState, screenReaderEnabledState } from '@store';

import { styles } from './styles';

import { Map, MapHomePinSize, MapLegend, ZoomPreset } from '../../../components/map/Map';
import { VideoContext } from '../../../hooks/useIsVideoFocused';
import { ChangeMapButton } from '../ChangeMapButton/ChangeMapButton';
import { tPath } from '../constants';
import { showAlerts } from '../helpers';
import { MapAlertsRow } from '../MapAlertsRow/MapAlertsRow';

type MapPageContentProps = {
  useDefaultLocation?: string;
};

type UpdateLegendState = (
  mapInventoryLoading: unknown,
  frameTime: number,
  frameNumber: number,
  maxFrameNumber: number,
) => void;

export const MapPageContent: FC<MapPageContentProps> = ({ useDefaultLocation }) => {
  const [mapType] = useRecoilState(mapTypeState);

  // useDefaultLocation to show overlay alerts when navigating from Alert Overlay modal
  const alerts = useWeatherMapAlerts(3, useDefaultLocation);

  const changeMapButtonReference = useContextReference(mapPageTestIDs.changeMapButton);

  const legendReference = useRef<{ updateLegendState: UpdateLegendState }>();
  const isFocused = useIsFocused();
  const [alertListHandle, changeMapButtonHandle, legendSectionHandle] = useContextNodeHandles([
    mapPageTestIDs.alertTilesList,
    mapPageTestIDs.changeMapButton,
    mapPageTestIDs.mapLegendSection,
  ]);

  const screenReaderEnabled = useRecoilValue(screenReaderEnabledState);
  const onChangeMapButtonPress = () => {
    navigationService.navigate(mapPageTestIDs.changeMapPage, { useDefaultLocation });
  };

  const { t } = useTranslation();

  return (
    <View>
      <PIPWidget
        height={160}
        pageID={mapPageTestIDs.mapPage}
        position={'topRight'}
        testID={mapPageTestIDs.pipWidget}
        videoContext={VideoContext.map}
        width={284}
      />
      <View importantForAccessibility={'no-hide-descendants'} style={styles.mapContainer}>
        <Map.API
          animate={isFocused}
          attributionStyle={styles.mapAttribution}
          homePinSize={mapType.zoom === ZoomPreset.national ? MapHomePinSize.small : MapHomePinSize.large}
          isVisibleOverride
          overlayType={mapType.overlay}
          style={styles.map}
          useDefaultLocation={useDefaultLocation}
          zoomPreset={mapType.zoom}
          onFrameChange={(
            mapInventoryLoading: unknown,
            frameTime: number,
            frameNumber: number,
            maxFrameNumber: number,
          ) => {
            if (legendReference?.current != null) {
              legendReference.current.updateLegendState(!mapInventoryLoading, frameNumber, maxFrameNumber, frameTime);
            }
          }}
        />
      </View>
      <View style={styles.bottomContent}>
        <View style={styles.alertsContainer}>
          <MapAlertsRow alerts={alerts} overlayType={mapType.overlay} zoomPreset={mapType.zoom} />
        </View>
        <View style={styles.mapControlsContainer}>
          <ChangeMapButton
            accessibilityHint={t(`${tPath}changeMapButton.accessibilityHint`)}
            accessibilityLabel={t(`${tPath}changeMapButton.accessibilityLabel`)}
            nextFocusDown={changeMapButtonHandle}
            nextFocusLeft={changeMapButtonHandle}
            nextFocusRight={screenReaderEnabled ? legendSectionHandle : changeMapButtonHandle}
            nextFocusUp={showAlerts(mapType.overlay, mapType.zoom, alerts) ? alertListHandle : changeMapButtonHandle}
            testID={mapPageTestIDs.changeMapButton}
            onPress={onChangeMapButtonPress}
          />
          <TWCAccessibleContent
            disabledScreenreaderFocus={changeMapButtonReference}
            nextFocusDown={null}
            nextFocusLeft={changeMapButtonHandle}
            nextFocusRight={null}
            nextFocusUp={null}
            testID={mapPageTestIDs.mapLegendSection}
          >
            <MapLegend animationLoading={false} overlayType={mapType.overlay} ref={legendReference} />
          </TWCAccessibleContent>
        </View>
      </View>
    </View>
  );
};
